import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "@/lib/LangContext";
import { LangSwitch } from "@/components/LangSwitch";
import { LogoLockup } from "@/components/Logo";

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "products", href: "#products", label: t.nav.products },
    { key: "services", href: "#services", label: t.nav.services },
    { key: "about", href: "#about", label: t.nav.about },
    { key: "faq", href: "#faq", label: t.nav.faq },
    { key: "contact", href: "#contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-slate-950/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <a
          data-testid="brand-link"
          href="#top"
          className="flex items-center gap-2"
        >
          <LogoLockup size={30} />
        </a>

        <nav className="hidden md:flex items-center gap-8 font-mono-x text-xs uppercase tracking-widest">
          {links.map((l) => (
            <a
              key={l.key}
              data-testid={`nav-link-${l.key}`}
              href={l.href}
              className="link-underline text-slate-300 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch />
          <a
            data-testid="nav-cta"
            href="#contact"
            className="hidden sm:inline-block bg-brand-500 text-black px-4 py-2 font-mono-x text-xs uppercase tracking-widest font-bold hover:bg-white transition-colors duration-200"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-slate-950/95 border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4 font-mono-x text-sm uppercase tracking-widest">
              {links.map((l) => (
                <a
                  key={l.key}
                  data-testid={`mnav-link-${l.key}`}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-slate-300 hover:text-brand-500"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
